import React, { Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useBookmark from '../../../hooks/bookmarks/useBookmark';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import playerService from '../../../helpers/bitmovinPlayer';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import { useQuery } from '@tanstack/react-query';
import { Button as IntigralButton, Spinner, Text, toast } from '@intigral/shared';
import { TimeMode } from 'bitmovin-player';
import { Button } from 'bitmovin-player-ui';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import mpx from '../../../actions/mpx_data';
import VisitAppScreen from '../../elements/VisitAppScreen';
import {
    KIDSMODE,
    playerTypes,
    ROUTE_CONSTANT,
    SCREEN_NAME_MAPPING,
    screenDetailTypes,
} from '../../../config/constants';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getMixpanelVodProperties } from '../../../contexts/MixpanelHelper';
import browser from '../../../helpers/browser';
import cdnHandler from '../../../helpers/CDNHandler';
import h, { getCountry, getLearnActionConfig } from '../../../helpers/common';
import f from '../../../helpers/formatter2';
import subscription from '../../../helpers/subscription';
import TimerQueue from '../../../helpers/timer-queue';
import url from '../../../helpers/url';
import user from '../../../helpers/user';
import viewEvents from '../../../helpers/viewEvents';
import useAvod from '../../../hooks/useAvod';
import useCdnBalancer from '../../../hooks/useCdnBalancer';
import useNetwork from '../../../hooks/useNetwork';
import useScreenHeight from '../../../hooks/useScreenHeight';
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';
import { __ } from '../../../translations';
import ConfirmPopup from '../../elements/ConfirmPopup';
import LikeDislikePopup from '../../elements/LikeDislikePopup';
import { GenericBitmovinPlayer } from '../../../features';
import Loader from '../../Loader';
import EpisodesCarousel from './EpisodesCarousel';
import Header from './Header';
import useSkipButtons from './useSkipButtons';
import _ from 'lodash';
import { useTvod } from '../../../hooks/useTvod';
import { useEpisodeActions } from './useEpisodeAction';
import { useMpxItemFeedQuery, usePlaylistQuery, useSeasonEpisodesQuery } from './useMPXQueries';
const tqueue = new TimerQueue();

// var seriesPlaylist = null;
// var selectedSeasonId =;?///';;.;';';;//;;/ null;[=]

/**
 * Resolve language names
 * Eg. https://bitmovin.com/demos/4k
 *
 * @todo This function seems unused, consider removing it if confirmed.
 */
// export const getLanguageLabels = function (data) {
//     return __(data.lang);
// };

export const VodPlayer = (props) => {
    const [searchParams] = useSearchParams();
    const encodedId = searchParams.get('id');
    const byId = encodedId ? atob(encodedId) : null;
    // const { bookmarks, isLoading } = useSelector((state) => state.bookmarks);
    const { addBookmark, removeBookmark, isLoading, bookmarks } = useBookmark();
    const [selectedSeasonId, setSelectedSeasonId] = useState(null);

    const {
        data: content,
        isLoading: isContentLoading,
        isError: isContentError,
        error: contentError,
    } = useMpxItemFeedQuery(byId);
    const { data: seasonsList, isLoading: areSeasonsLoading } = usePlaylistQuery(content);

    const { data: seriesPlaylist, isLoading: areEpisodesLoading } = useSeasonEpisodesQuery(selectedSeasonId, content);

    // Sort seasons based on pl1$seasonOrderDescending flag (CTA)
    const sortedSeasonsList = useMemo(() => {
        if (!seasonsList || seasonsList.length === 0) return seasonsList;
        if (content?.pl1$seasonOrderDescending === true) {
            return [...seasonsList].reverse();
        }
        return seasonsList;
    }, [seasonsList, content?.pl1$seasonOrderDescending]);

    const seasonNumberFromUrl = searchParams.get('season');

    useEffect(() => {
        // Wait until we have the seasons and bookmark data
        if (!sortedSeasonsList || sortedSeasonsList.length === 0 || isLoading) {
            return;
        }

        // Logic to determine the default/initial season ID
        let initialSeasonId = null;

        const bookmarkedSeasonNumber = bookmarks && bookmarks.length > 0 ? getLatestWatchedSeason(bookmarks) : 0;

        if (seasonNumberFromUrl) {
            const seasonFromUrl = sortedSeasonsList.find(
                (s) => parseInt(s.tvSeasonNumber, 10) === parseInt(seasonNumberFromUrl, 10)
            );
            if (seasonFromUrl) initialSeasonId = seasonFromUrl.Id;
        }

        if (!initialSeasonId && bookmarkedSeasonNumber > 0) {
            const bookmarkedSeason = sortedSeasonsList.find(
                (s) => parseInt(s.tvSeasonNumber, 10) === parseInt(bookmarkedSeasonNumber, 10)
            );
            if (bookmarkedSeason) initialSeasonId = bookmarkedSeason.Id;
        }

        // Fallback to the first season in the sorted list
        if (!initialSeasonId) {
            initialSeasonId = sortedSeasonsList[0].Id;
        }

        setSelectedSeasonId(initialSeasonId);
    }, [sortedSeasonsList, bookmarks, isLoading, seasonNumberFromUrl]);

    const contentType = content ? content.programType : null;

    const streamUrl = content?.programType === screenDetailTypes.MOVIES ? content.streamingUrl : null;

    const [video, setVideo] = useState(null);
    const [defaultEpisodeIndex, setDefaultEpisodeIndex] = useState(null);
    const [selectedSeasonNumber, setSelectedSeasonNumber] = useState(null);
    const [deletedBookmark, setDeletedBookmark] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState(null);
    let deletedBookmarkFlag = false;

    const { navigateToEpisode } = useEpisodeActions();
    // TODO: move to EppisodesCarousel
    const [swiper, updateSwiper] = useState(null);
    const [popupElements, setPopupElements] = useState(null);
    const { track } = useContext(MixpanelContext);
    // Using React Query instead of Redux for configuration
    const { paidVsFree, config, video: videoConfig } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable;
    const [navigateTo, setNavigateTo] = useState('');

    const userRating = useSelector((state) => state?.profile?.selectedProfile?.parentalControlValues) || {
        0: 'G',
        1: 'PG',
        2: 'PG12',
        3: 'PG15',
        4: 'R15',
        5: 'R18',
    };
    const autoPlayNextEpisode = useSelector((state) => state?.profile?.selectedProfile?.autoPlayNextEpisode);
    const { dispatchedActions } = useSelector((state) => state.profile);

    //  Configuration already destructured above
    const bookmarksInterval = config?.global?.playback?.bookmarkInterval ?? 10;

    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [likeDislikeObject, setlikeDislikeObject] = useState({
        isOpenLikeDislikePopup: false,
        backNavigationUrl: '',
        contentGuid: '',
    });

    // TODO: move to EppisodesCarousel
    const carouselRef = useRef(null);
    let vodPlayerContainerRef = useRef(null);
    let SeriesContainer = useRef(null);
    var playlist = [];
    const isSafari = browser.isSafari();
    let completedTracked = false;
    const tvShowSection = useRef(null);
    const navigate = (url.navigate = useNavigate());
    const navigationType = useNavigationType();
    const location = useLocation();
    const screenHeight = useScreenHeight();
    const waterMarkRef = useRef(null);

    // Sometimes the user close the video before the response is finished, thus we need to ignore the response after it's closed.
    let ignoreResponse = false;

    const [isOnline, isAwakeFromSleep] = useNetwork();
    const learnActionConfig = getLearnActionConfig();
    const cdnBalancer = useCdnBalancer();
    const { isTvod } = useTvod(content);

    useEffect(() => {
        if (isOnline === null || isOnline === undefined || !playerService.player) return;

        if (tqueue) {
            tqueue.clear();
        }
    }, [isOnline]);

    const isPlayerLoading =
        isLoading ||
        isContentLoading ||
        ((contentType === screenDetailTypes.SERIES || contentType === screenDetailTypes.EPISODE) && !seriesPlaylist) ||
        ((contentType === screenDetailTypes.SERIES || contentType === screenDetailTypes.EPISODE) &&
            defaultEpisodeIndex === null);

    const getBookmarkedEpisodeToPlay = (bookmarks, episodes, playlist) => {
        var seriesBookmarkedEpisode = bookmarks?.find((a) => a.seriesGUID === atob(url.getQuery('guid')));
        if (seriesBookmarkedEpisode) {
            const bookmarkedEpisodeInPlaylist =
                bookmarks && bookmarks.length > 0 ? episodes.find((a) => a.Id === seriesBookmarkedEpisode.Id) : null;
            if (bookmarkedEpisodeInPlaylist && bookmarkedEpisodeInPlaylist.Id !== null) {
                return bookmarkedEpisodeInPlaylist;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    };

    const currentEpisode = useMemo(() => {
        if (!seriesPlaylist || seriesPlaylist?.length === 0 || isLoading) return null;

        const normalizeId = (id) => String(id).split('/').pop();

        const episodeIdFromUrl = normalizeId(atob(searchParams.get('id')));
        let active = seriesPlaylist.findIndex((ep) => normalizeId(ep.Id) === episodeIdFromUrl);

        if (active !== -1) {
            return seriesPlaylist[active];
        }

        // Fallback logic for bookmarked or default episode
        const bookmarkedEpisode = getBookmarkedEpisodeToPlay(bookmarks, seriesPlaylist);
        if (bookmarkedEpisode) {
            return bookmarkedEpisode;
        }

        return seriesPlaylist[0]; // Default to the first episode of the season
    }, [seriesPlaylist, searchParams, bookmarks, isLoading]);

    // The final asset to be passed to the player
    const getAsset = useMemo(() => {
        if (selectedEpisode) return selectedEpisode;
        if (contentType === screenDetailTypes.MOVIES) return content;
        if (contentType === screenDetailTypes.SERIES || contentType === screenDetailTypes.EPISODE)
            return currentEpisode;
        return null;
    }, [content, contentType, currentEpisode, selectedEpisode]);

    const vodItemPackages =
        content && Object.prototype.hasOwnProperty.call(content, 'subscriptionGuids')
            ? content.subscriptionGuids
            : null;

    const { isAdsEnabled, setupAVODParams, checkForAds, setupAVODParamsCustomForAsset } = useAvod(
        playerTypes.Vod,
        isTvod,
        getAsset
    );

    useEffect(() => {
        if (!isAwakeFromSleep) return;

        if (tqueue) {
            tqueue.clear();
        }
    }, [isAwakeFromSleep]);

    useEffect(() => {
        if (navigateTo) navigate(navigateTo);
    }, [navigateTo]);

    // TODO: move to EppisodesCarousel
    useEffect(() => {
        if (carouselRef.current?.swiper) {
            updateSwiper(carouselRef.current?.swiper);
        }
    }, [carouselRef.current]);

    const dispatch = useDispatch();
    //  Config already retrieved above

    const bookmarkSettings = h.getBookmarkSettings();
    const bookmarkingRemovalPercentage = bookmarkSettings.removalPercentage;
    const bookmarkWithDelete = bookmarkSettings.withDelete;

    const renderMessage = ({ msgTitle, msgText, msgButtonText, clickURL }) => {
        return (
            <>
                <Text as="h2" variant="h2" margin={{ default: '2x', md: '3x' }}>
                    {msgTitle}
                </Text>
                <Text as="p" alignment="center" color="gray" margin={{ default: '2x', md: '4x' }}>
                    {msgText}
                </Text>
                {user.getProfileMode() !== KIDSMODE.KID && (
                    <IntigralButton
                        style={{ width: '100%' }}
                        LinkComponent="a"
                        href={clickURL}
                        onClick={(e) => {
                            setPopupElements(false);
                        }}
                    >
                        {msgButtonText}
                    </IntigralButton>
                )}{' '}
            </>
        );
    };

    const getPopupMsg = (msgTitle, msgText, msgButtonText, clickURL) => {
        const messageComponent = renderMessage({ msgTitle, msgText, msgButtonText, clickURL });
        viewEvents.broadcast('openPopup', { component: messageComponent, screen: 'vodplayer' });
    };

    const checkUserAllowedToPlay = (vodItemPackages, ratings) => {
        if (
            vodItemPackages !== null &&
            subscription.checkItemBelongsToAddonsPackages(vodItemPackages) &&
            !subscription.checkItemBelongsToActivePackages(vodItemPackages)
        ) {
            if (enableLockIcon) {
                dispatch(showUpgradedModal(true));
            } else {
                getPopupMsg(
                    __('loggedInNoExtraAddon_popup_header'),
                    __('loggedInNoExtraAddon_popup_description'),
                    __('loggedInNoExtraAddon_popup_button_title'),
                    user.getUserProfileUrl()
                );
            }

            return false;
        }
        if (!subscription.checkItemBelongsToActivePackages(vodItemPackages)) {
            if (enableLockIcon) {
                dispatch(showUpgradedModal(true));
            } else {
                getPopupMsg(
                    __('loggedInUnSub_popup_header'),
                    __('loggedInUnSub_popup_description1'),
                    __('loggedInUnSub_popup_actionBtn_title'),
                    user.getUserProfileUrl()
                );
            }
            return false;
        }
        // Check if movie rating is matching user parental control
        if (!Object.values(userRating).includes(ratings?.toUpperCase()) && h.isDeviceSupported()) {
            getPopupMsg(
                __('livetv_content_not_allowed_title'),
                __('livetv_content_not_allowed_desc'),
                __('parental_control_popup_btn_text'),
                url.route('profile-settings')
            );
            return false;
        }
        return true;
    };

    const context = h.getAppData();

    const getLatestWatchedSeason = (list) => {
        const bookmarkedEpisode = list.find((a) => a.seriesGUID === atob(url.getQuery('guid')));
        if (bookmarkedEpisode) {
            return bookmarkedEpisode.seasonNumber;
        } else {
            return 0;
        }
    };

    useEffect(() => {
        // if (!isLoading) {
        // loadProfileBookmarksData();
        // }
        cdnHandler.init(config);
    }, []);

    useEffect(() => {
        // load profile and close popup after password Verified
        if (dispatchedActions && dispatchedActions.action === 'hasBeenDeleted') {
            setShowConfirmPopup(true);
        }
    }, [dispatchedActions]);

    useEffect(() => {
        if (!currentEpisode || !seriesPlaylist) return;

        const index = seriesPlaylist.findIndex((ep) => ep.Id === currentEpisode.Id);
        if (index !== -1) {
            setDefaultEpisodeIndex(index);
        } else {
            setDefaultEpisodeIndex(0);
        }
    }, [currentEpisode, seriesPlaylist]);

    function redirectOnPlayerError(toastMessage = '', redirectDuration = 6000) {
        if (toastMessage) toast(__(toastMessage), 'error');
        setTimeout(() => {
            if (navigationType !== 'POP') {
                navigate(-1);
                return;
            }
            const PAGE = url.getCurrentPageName();
            if (PAGE === SCREEN_NAME_MAPPING.livetv) {
                setNavigateTo(url.route(ROUTE_CONSTANT.LIVE_TV));
                return;
            }
            setNavigateTo(url.route(''));
        }, redirectDuration);
    }

    const onPlayingManageBookmarks = (contentOjb) => {
        if (!playerService.player) return;
        setTimeout(() => {
            if (!playerService.player) return;
            track(`${h.getScreen()} - ${EVENT.VOD_PLAYER.START}`, {
                ...getMixpanelVodProperties(content),
                'VOD Duration': f.converToHHMMSS(playerService?.player?.getDuration()),
                'Video Type': 'Movie',
            });
        }, 500);
        var bookmarkAdded = false;
        var object = contentOjb.programType === screenDetailTypes.MOVIES ? contentOjb : currentEpisode;
        playerService.player.addEventListener('timechanged', function () {
            if (!playerService.player) return;
            var content = contentOjb.programType === screenDetailTypes.MOVIES ? contentOjb : currentEpisode;
            var timeCode =
                playerService?.player?.getCurrentTime(TimeMode.RelativeTime) !== null
                    ? Math.ceil(playerService?.player?.getCurrentTime(TimeMode.RelativeTime))
                    : 0;
            var duration = Math.ceil(playerService?.player?.getDuration());
            var durationPercentage = bookmarkWithDelete ? bookmarkingRemovalPercentage : 100;
            var maximumDuration = (duration * durationPercentage) / 100;

            // Add bookmark

            // check if there is ads and if the timecode is less than the ads duration then don't add bookmark
            const adsData = playerService.getAdsData();
            if (adsData != null && timeCode < adsData.duration / 1000) {
                return null;
            }

            if (
                !playerService.player.isPaused() &&
                timeCode > 0 &&
                timeCode < maximumDuration &&
                playerService.player.getBufferedRanges()
            ) {
                if (timeCode % bookmarksInterval === 0) {
                    if (bookmarkAdded === false && object.Id === content.Id) {
                        addBookmark(content, String(timeCode), parseInt(content.duration, 10));
                        bookmarkAdded = true;
                    }
                } else {
                    bookmarkAdded = false;
                }
            }
            // RemoveBookmark;
            if (
                bookmarkWithDelete &&
                !playerService.player.isPaused() &&
                timeCode > 0 &&
                timeCode >= maximumDuration &&
                playerService.player.getBufferedRanges()
            ) {
                if (deletedBookmarkFlag === false) {
                    removeBookmark({ bookmark: content });
                    setDeletedBookmark(true);
                    deletedBookmarkFlag = true;
                }
            }
        });
    };
    const onPlayPause = (e) => {
        const { paused } = e;
        if (paused) {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.PAUSE_CLICK, {});
        } else {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.PLAY_CLICK, {});
        }
    };

    const playNextEpisode = async (force = false) => {
        if (!force && autoPlayNextEpisode === false) {
            return;
        }

        const normalizeId = (id) => String(id).split('/').pop();
        const currentIdFromUrl = normalizeId(atob(url.getQuery('id')));

        if (!Array.isArray(seriesPlaylist) || seriesPlaylist.length === 0 || isLastEpisodeAcrossSeasons()) {
            return;
        }

        let currentEpisodeIndex = seriesPlaylist.findIndex((ep) => normalizeId(ep.Id) === currentIdFromUrl);

        if (currentEpisodeIndex === -1) {
            currentEpisodeIndex = defaultEpisodeIndex ?? 0;
        }

        const nextEpisodeIndex = currentEpisodeIndex + 1;
        const nextEpisode = seriesPlaylist[nextEpisodeIndex];

        if (nextEpisode) {
            setDeletedBookmark(false);
            sessionStorage.setItem('ItemChanging', true);
            deletedBookmarkFlag = false;
            playerService.player.castStop();

            waterMarkRef.current?.removeWatermarkOverlay();

            await navigateToEpisode(nextEpisode, 'Auto');
        } else {
            const currentEpisode = seriesPlaylist[currentEpisodeIndex];
            const navigateBackURL = url.route(url.getDetailsPage(currentEpisode.programType), {
                id:
                    currentEpisode.programType === screenDetailTypes.EPISODE
                        ? btoa(currentEpisode.seriesId)
                        : btoa(currentEpisode.Id),
                guid:
                    currentEpisode.programType === screenDetailTypes.EPISODE
                        ? btoa(currentEpisode.seriesId.split('/').pop())
                        : btoa(currentEpisode.guid),
            });

            if (learnActionConfig?.isLikeDislikeEnable) {
                openLikeDislikePopup(navigateBackURL, currentEpisode);
            } else {
                navigate(-1);
            }
        }
    };

    const onBuffering = (selected) => {
        track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.BUFFERING, getMixpanelVodProperties(selected));
        completedTracked = false;
    };
    var errorTracked = false;
    const onError = (error) => {
        if (!errorTracked) {
            errorTracked = true;

            setVideo({ error: error });
            const errorEl = document.querySelector('.crash-error-live');
            if (errorEl) errorEl.style.display = 'none';

            SeriesContainer.current.style.display = 'none';
            // document.querySelector('.vjs-tech').style.display = 'none';
            document.querySelector('.vjs-loading-spinner').style.display = 'none';
            document.querySelector('.bitmovinplayer-container').style.overflow = 'hidden';
            playerService.player.castStop();
            closeOverlay();
            completedTracked = false;
        }
    };

    const onSuccess = () => {
        // document.querySelector('.vjs-tech').style.display = 'block';
        var liElems = document.querySelectorAll('.crash-error-live');
        [].filter.call(liElems, function (el) {
            el.style.display = 'none';
        });
        const movieTitleElement = document.querySelector('.movie-title');
        if (movieTitleElement) {
            movieTitleElement.style.display = 'block';
        }
        document.querySelector('.bitmovinplayer-container').style.overflow = 'auto';
    };
    const onSeeked = () => {
        track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.SEEK, {});
    };

    // could this moved to <Header /> component?
    const getCurrentSeason = () => {
        if (sortedSeasonsList && sortedSeasonsList.length) {
            for (let i in sortedSeasonsList) {
                if (selectedSeasonId === sortedSeasonsList[i].Id) {
                    return __('tv_series_details_season') + ' ' + sortedSeasonsList[i].tvSeasonNumber + ' - ';
                }
            }
        }
    };

    const onSubtitlesView = (e) => {
        const { menu } = e;
        if (menu) {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.AUDIO_SUBTITLES_CLICK, {});
        } else {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.AUDIO_SUBTITLES_CANCEL, {});
        }
    };

    const onAudioView = (e) => {
        const { menu } = e;
        if (menu) {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.AUDIO_SUBTITLES_CLICK, {});
        } else {
            track(h.getScreen() + ' - ' + EVENT.VOD_PLAYER.AUDIO_SUBTITLES_CANCEL, {});
        }
    };

    const closeOverlay = () => {
        if (SeriesContainer.current) {
            SeriesContainer.current.style.display = 'none';
        }
        safePlayerPlay(playerService.player);
    };

    const handleSectionClick = (elm) => {
        if (elm.className === 'SeriesContainer') {
            closeOverlay();
        }
    };

    const toggleOverlay = () => {
        if (!SeriesContainer.current) return;

        if (SeriesContainer.current.style.display === 'none') {
            SeriesContainer.current.style.display = 'initial';
            playerService.player.pause();
        } else {
            SeriesContainer.current.style.display = 'none';
            safePlayerPlay(playerService.player);
        }
    };

    const isLastEpisodeAcrossSeasons = () => {
        if (!sortedSeasonsList?.length || !seriesPlaylist?.length) return false;

        const currentEpisodeId = atob(url.getQuery('id'));

        const selectedSeasonNumber = seriesPlaylist?.[0]?.tvSeasonNumber ?? 1;

        const currentSeasonEpisodes = seriesPlaylist.filter((ep) => ep.tvSeasonNumber === selectedSeasonNumber);
        if (!currentSeasonEpisodes.length) return false;

        const currentIndex = currentSeasonEpisodes.findIndex((ep) => ep.Id === currentEpisodeId);
        if (currentIndex === -1) return false;

        const isLastEpisodeInSeason = currentIndex === currentSeasonEpisodes.length - 1;
        const totalSeasons = sortedSeasonsList.length;
        const lastSeasonNumber = Math.max(...sortedSeasonsList.map((season) => season.tvSeasonNumber));
        const isLastSeason = selectedSeasonNumber === lastSeasonNumber;

        if (totalSeasons === 1 && isLastEpisodeInSeason) return true;

        if (totalSeasons > 1 && isLastSeason && isLastEpisodeInSeason) return true;

        if (totalSeasons === 3 && selectedSeasonNumber === 3 && isLastEpisodeInSeason) return true;

        return false;
    };

    const { renderSkipIntro, renderSkipOutro, renderWatchCredit } = useSkipButtons({
        content,
        playNextEpisode,
        currentEpisode,
        seriesPlaylistLength: seriesPlaylist?.length ?? 0,
        isLastEpisodeAcrossSeasons: isLastEpisodeAcrossSeasons(),
    });

    /**
     * Render playlist button in player
     */
    const renderPlaylistButton = (player) => {
        let myPlaylistButton = new Button({ cssClass: 'fa custom-info-icon ui-playlistbutton bmpui-ui-button' });
        myPlaylistButton.onClick.subscribe(function () {
            toggleOverlay();
        });

        highlightCurrentEpisode();

        /**
         * Highlights the currently playing episode in the playlist UI.
         */
        // TODO: `player` is `playerService.player`, which is still null when the player UI is built, so this
        // `playing` listener is never attached. Attach it in the button's `configure(player)` instead, like
        // `SkipButton` in useSkipButtons.ts.
        function highlightCurrentEpisode() {
            if (player) {
                player.addEventListener('playing', function () {
                    var active = h.getIndexOfArrayByAttrVal(seriesPlaylist, 'Id', atob(url.getQuery('id')));
                    if (active === -1) {
                        active = defaultEpisodeIndex;
                    }
                        swiper.slides[active - 1].classList.remove('currentEpisode');
                    swiper.slideTo(active);
                    swiper.slides[active].classList.add('currentEpisode');
                    completedTracked = false;
                });
            }
        }

        return myPlaylistButton;
    };

    const onCompleted = (selected) => {
        if (!completedTracked) {
            completedTracked = true;
            track(`${h.getScreen()} - ${EVENT.VOD_PLAYER.COMPLETED}`, {
                ...getMixpanelVodProperties(selected),
                PlayTime: f.convertSecondsToMinutes(selected.duration),
                'Video Type': 'Movie',
            });
            if (selected.programType === screenDetailTypes.MOVIES) {
                playerService.player.castStop();
                var navigateBackURL = url.route(url.getDetailsPage(selected.programType), {
                    id:
                        selected.programType === screenDetailTypes.EPISODE
                            ? btoa(selected.seriesId)
                            : btoa(selected.Id),
                    guid:
                        selected.programType === screenDetailTypes.EPISODE
                            ? btoa(selected.seriesId.split('/')[selected.seriesId.split('/').length - 1])
                            : btoa(selected.guid),
                });
                if (learnActionConfig?.isLikeDislikeEnable) {
                    openLikeDislikePopup(navigateBackURL, selected);
                } else {
                    // navigate(navigateBackURL);
                    navigate(-1);
                }
            } else {
                playNextEpisode();
            }
        }
    };

    const openLikeDislikePopup = (_navigateBackURL, _content) => {
        const seriesGuid = _content?.seriesId
            ? _content.seriesId.split('/')[_content.seriesId.split('/').length - 1]
            : _content.guid;
        setlikeDislikeObject({
            isOpenLikeDislikePopup: true,
            backNavigationUrl: _navigateBackURL,
            contentGuid: seriesGuid,
        });
    };
    const closeLikeDislikePopup = () => {
        setlikeDislikeObject({
            ...likeDislikeObject,
            isOpenLikeDislikePopup: false,
        });
        navigate(-1);
        // navigate(likeDislikeObject.backNavigationUrl);
    };

    const getVodBookmarkedItemTimeCode = (bookmarks, item) => {
        var bookmarkedVodBookmarks =
            bookmarks && bookmarks.length > 0 ? _.find(bookmarks, (a) => a?.paId === item?.Id) : null;
        if (bookmarkedVodBookmarks) {
            return bookmarkedVodBookmarks.timecode;
        } else {
            return 0;
        }
    };

    // could this be moved to <Header /> component?
    const closePlayer = (e) => {
        navigate(-1);
        sessionStorage.removeItem('selectedAudioLang');
        const initialLocation = context?.route?.history?.location?.pathname;
        const currentLocation = location?.pathname;

        if (initialLocation != currentLocation) {
            e?.preventDefault();
        }
    };

    // TODO: not needed?
    // useEffect(() => {
    //     if (!tvShowSection.current) return;

    //     const nextBtn = tvShowSection.current?.querySelector('.swiper-button-next');
    //     const prevBtn = tvShowSection.current?.querySelector('.swiper-button-prev');

    //     const nextBtnlistener = () => swiper.slideNext();
    //     nextBtn?.addEventListener('click', nextBtnlistener);
    //     const prevBtnlistener = () => swiper.slidePrev();
    //     prevBtn?.addEventListener('click', prevBtnlistener);

    //     return () => {
    //         ignoreResponse = true;
    //         nextBtn?.removeEventListener('click', nextBtnlistener);
    //         prevBtn?.removeEventListener('click', prevBtnlistener);
    //     };
    // });

    if (content && !checkUserAllowedToPlay(vodItemPackages, content.ratings)) {
        redirectOnPlayerError('player_upgrade_to_view_content');
        return;
    }

    if (!h.isDeviceSupported() || (h.isMobileAndTablet() && !h.isAllowedMobileStreaming())) {
        return (
            <div className="VisitAppScreen">
                <VisitAppScreen content={content} />
            </div>
        );
    }

    if (isPlayerLoading) {
        return <Spinner opaqueBg />;
    }

    if (!content || !h.isDeviceSupported()) {
        return (
            <div className="VisitAppScreen">
                <VisitAppScreen content={content} />
            </div>
        );
    }

    const asset = content.programType === screenDetailTypes.MOVIES ? content : currentEpisode;

    return (
        (learnActionConfig?.isLikeDislikeEnable && likeDislikeObject?.isOpenLikeDislikePopup === true && (
            <LikeDislikePopup
                itemGuid={likeDislikeObject?.contentGuid || ''}
                item={{ ...asset }}
                closeLikeDislikePopup={closeLikeDislikePopup}
            />
        )) || (
            <div ref={vodPlayerContainerRef} style={{ height: screenHeight }}>
                <Suspense fallback={<Loader />}>
                    <GenericBitmovinPlayer
                        key={asset?.Id}
                        streamingUrl={streamUrl}
                        VODAsset={asset}
                        content={content}
                        playerType={playerTypes.Vod}
                        playlist={seriesPlaylist}
                        defaultEpisodeIndex={defaultEpisodeIndex}
                        contentType={contentType}
                        bookmarkedVodTimeCode={() => getVodBookmarkedItemTimeCode(bookmarks, asset)}
                        onPlaying={() => onPlayingManageBookmarks(content)}
                        onPlayPause={onPlayPause}
                        onAudioView={onAudioView}
                        onSubtitlesView={onSubtitlesView}
                        onBuffering={() => onBuffering(asset)}
                        onSeeked={onSeeked}
                        onCompleted={() => onCompleted(asset)}
                        onError={(error, defaultMessage) => {
                            onError({
                                title:
                                    __(error + '_title') === error + '_title'
                                        ? __('general_error_message')
                                        : __(error + '_title'),
                                description:
                                    __(error + '_desc') === error + '_desc' ? defaultMessage : __(error + '_desc'),
                                status: true,
                                source: 'VOD',
                            });
                        }}
                        onWatermarkReady={(watermark) => {
                            waterMarkRef.current = watermark;
                        }}
                        manageAudioProfile
                        renderCustomButton={sortedSeasonsList ? renderPlaylistButton : null}
                        renderSkipIntro={renderSkipIntro}
                        renderSkipOutro={renderSkipOutro}
                        renderWatchCredit={renderWatchCredit}
                        isTvod={isTvod}
                        header={
                            content && (
                                <Header
                                    isLikeDislikeEnabled={learnActionConfig?.isLikeDislikeEnable}
                                    videoError={video?.error}
                                    content={asset}
                                    headerText={
                                        <>
                                            {getCurrentSeason()}{' '}
                                            {content.programType === screenDetailTypes.MOVIES && content.text}
                                            {content.programType !== screenDetailTypes.MOVIES &&
                                                currentEpisode &&
                                                currentEpisode.text}
                                        </>
                                    }
                                    closePlayer={closePlayer}
                                />
                            )
                        }
                    >
                        {content.programType !== screenDetailTypes.MOVIES && (
                            <div
                                ref={SeriesContainer}
                                onClick={(e) => {
                                    handleSectionClick(e.target);
                                }}
                                className="child theo-top-controlbar theo-secondary-color"
                                style={{
                                    display: 'none',
                                    position: 'absolute',
                                    right: 0,
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    zIndex: 10000,
                                }}
                            >
                                <section
                                    className="SeriesContainer"
                                    ref={tvShowSection}
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        top: '0px',
                                        bottom: '0px',
                                        zIndex: 1,
                                        backgroundColor: 'rgba(0,0,0,.9)',
                                    }}
                                    onClick={(e) => {
                                        handleSectionClick(e.target);
                                    }}
                                >
                                    {!!sortedSeasonsList &&
                                        !!sortedSeasonsList.length &&
                                        !!seriesPlaylist &&
                                        !!seriesPlaylist?.length && (
                                            <EpisodesCarousel
                                                seriesPlaylist={seriesPlaylist}
                                                seasonsList={sortedSeasonsList}
                                                selectedSeasonId={selectedSeasonId}
                                                setSelectedSeasonId={setSelectedSeasonId}
                                                ref={carouselRef}
                                                closeOverlay={closeOverlay}
                                            />
                                        )}
                                </section>
                            </div>
                        )}
                    </GenericBitmovinPlayer>
                </Suspense>
                <ConfirmPopup
                    showPopup={showConfirmPopup}
                    popupAction={false}
                    autoRedirect
                    title={null}
                    description={__('manage_profile_your_profile_deleted')}
                />
            </div>
        )
    );
};
